import { z } from "zod";
import {
    JournalEntryLineSchema,
    manualJournalEntryLineBodySchema,
    validateDebitAndCreditSums,
} from "./journalEntry";

/**
 * The eliminating-entry body adds a per-line `entityId`: attribution to the
 * group member the line relates to. The authored account and entity are
 * persisted together while posting resolves independently to the overlay chart.
 */
export const eliminatingEntryLineBodySchema =
    manualJournalEntryLineBodySchema.and(
        z.object({
            entityId: z.number().int().positive(),
            // Narrowed only at this endpoint: eliminating lines are always
            // authored against real account rows, whose ids are uuids. The
            // shared manual-journal line schema stays a plain string because
            // legacy journal sources still carry non-uuid account ids.
            accountId: z.string().uuid(),
        }),
    );

/**
 * Manual eliminating entries are consolidation-group-scoped manual journal
 * entries posted to the group's overlay ledger. The body mirrors a manual
 * journal entry but carries no `ledgerId`/`ledgerBasis` — the target ledger is
 * always the group's overlay ledger, resolved server-side from the group.
 */
export const createEliminatingEntryBodySchema = z
    .object({
        date: z.string().date(),
        memo: z.string().nullable().optional(),
        lines: z.array(eliminatingEntryLineBodySchema).min(2).max(500),
        reversedAt: z.string().date().nullable().optional(),
    })
    .superRefine(validateDebitAndCreditSums);

export const updateEliminatingEntryBodySchema =
    createEliminatingEntryBodySchema;

export const eliminatingEntrySchema = z.object({
    id: z.string().uuid(),
    consolidationGroupId: z.string().uuid(),
    accountingLedgerId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    date: z.coerce.date(),
    memo: z.string().nullable(),
    lines: z.array(JournalEntryLineSchema),
    reversedAt: z.coerce.date().nullable(),
});

export const listEliminatingEntriesResponseSchema = z.object({
    eliminatingEntries: z.array(eliminatingEntrySchema),
});

export const eliminatingEntryMutationResponseSchema = z.object({
    eliminatingEntry: eliminatingEntrySchema,
});

export type CreateEliminatingEntryBody = z.infer<
    typeof createEliminatingEntryBodySchema
>;
export type UpdateEliminatingEntryBody = z.infer<
    typeof updateEliminatingEntryBodySchema
>;
export type EliminatingEntry = z.infer<typeof eliminatingEntrySchema>;
